import { Controller } from "@hotwired/stimulus"

// Reusable helper for forms whose fields live inside modals or tabs. Native HTML5 validation can't show
// feedback for a required/invalid field inside a CLOSED modal — the control is non-focusable, so the browser
// silently blocks submit. This keeps native validation on (no novalidate, no submit blocking): it listens
// for `invalid` (capture phase — the event does not bubble) and opens the modal the field lives in, so the
// browser can then focus it and show its own validation bubble.
//
// Server-side errors have the same problem: after a failed save the re-rendered form puts simple_form's inline
// error (and `aria-invalid="true"`) on a field that may sit in an unselected tab. On connect this selects that tab
// and scrolls the first such field into view (its modal, if any, is already rendered open — see list_toggle_row).
//
// Apply to any modal-backed form: data: { controller: "reveal-invalid" }
export default class extends Controller {
  connect() {
    this.reveal = this.reveal.bind(this)
    this.element.addEventListener("invalid", this.reveal, true)
    // Let the tabs controller inside the form connect first.
    requestAnimationFrame(() => this.revealServerError())
  }

  disconnect() {
    this.element.removeEventListener("invalid", this.reveal, true)
  }

  reveal(event) {
    this.openModalFor(event.target)
  }

  revealServerError() {
    const field = this.element.querySelector('[aria-invalid="true"]')
    if (!field) return

    this.selectTabFor(field)
    field.scrollIntoView({ block: "center" })
  }

  selectTabFor(field) {
    const panel = field.closest('[data-tabs-target="tab"]')
    if (!panel) return

    const tabsElement = panel.closest('[data-controller~="tabs"]')
    tabsElement
      ?.querySelector(`[data-tabs-target="selection"][data-tabs-value="${panel.dataset.tabsValue}"]`)
      ?.click()
  }

  openModalFor(field) {
    const modal = field.closest('[data-controller~="modal"]')
    if (!modal) return

    this.application
      .getControllerForElementAndIdentifier(modal, "modal")
      ?.show({ preventDefault() {} })
  }
}
